import { Box, Stack, Switch, Typography } from "@mui/material";
import type { JSX } from "react";
import { useMemo, useState } from "react";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill.tsx";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import { useBiCommonDataContext } from "../../../contexts/BiCommonDataContext.ts";
import { reportSettingsSchema } from "../../../../../../../api/schemas/biClientSchema.ts";
import Conditions from "./conditions/Conditions.tsx";
import ViewFieldsCustomConditionsParameters from "./ViewFieldsCustomConditionsParameters.tsx";
import type { ConditionField, ReportField } from "../../../../../../../api/types/svTypes.ts";

interface Props {
  fieldsWithConditions: ReportField[];
  initialDetailedView: boolean | undefined;
  renderCustomId?: (field: ConditionField) => JSX.Element;
  getFieldCaption?: (field: ReportField) => string;
}

export default function ViewerParameters({
  fieldsWithConditions,
  initialDetailedView,
  renderCustomId,
  getFieldCaption,
}: Props) {
  const [detailedView, setDetailedView] = useState(
    () => reportSettingsSchema.parse({ detailedView: initialDetailedView }).detailedView
  );
  const { onConditionUpdate, conditions } = useBiCommonDataContext();
  const parameters = useMemo(
    () => conditions.filter((c) => c.config.parameter === true && !c.validation?.invalid),
    [conditions]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", px: "1.4rem", py: "1rem", gap: 1, width: "350px" }}>
      <Stack direction={"row"} sx={{ alignItems: "center", gap: 1, pb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: "text.primary" }}>
          Parameters
        </Typography>
        <HorizontalFill />
        <Typography>Detailed view</Typography>
        <Switch checked={detailedView} onChange={(_, value) => setDetailedView(value)} />
      </Stack>
      <ScrollableFlexContainer>
        <Conditions
          conditions={parameters}
          canBeRemoved={false}
          onFieldChanged={onConditionUpdate}
          disableEqualitySelection={true}
          renderCustomId={renderCustomId}
          sx={{ mb: 1 }}
        />
        <ViewFieldsCustomConditionsParameters
          fields={fieldsWithConditions}
          detailedView={detailedView}
          getFieldCaption={getFieldCaption}
        />
      </ScrollableFlexContainer>
    </Box>
  );
}
